import { Plus, Trash2 } from 'lucide-react';
import type { TrainingQuestionDef } from '@hcc/types';
import { Button } from '../../ui/Button';
import { SHARED_INPUT_CLASS } from './pageMeta';
import { QuestionCommonFields } from './QuestionCommonFields';
import { ImageUploadInput } from './ImageUploadInput';
import { genId } from './moduleOps';
import { SortableList, SortableRow, DragHandle, reorder } from './SortableList';

type ChoiceQuestion = Extract<TrainingQuestionDef, { type: 'single_choice' | 'multi_select' }>;

/**
 * Options editor for single-choice (radio: exactly one correct) and
 * multi-select (checkbox: one or more correct) questions.
 */
export function ChoiceQuestionEditor({
  question,
  onChange,
  graded = true,
}: {
  question: ChoiceQuestion;
  onChange: (next: ChoiceQuestion) => void;
  /** False hides the points box (an unmarked assessment). */
  graded?: boolean;
}) {
  const multi = question.type === 'multi_select';

  // The correct-answer fields are optional on the type (an ungraded module
  // authors none), so every read here defaults rather than assuming one.
  function isCorrect(id: string): boolean {
    return multi
      ? (
          (question as Extract<ChoiceQuestion, { type: 'multi_select' }>).correct_option_ids ?? []
        ).includes(id)
      : (question as Extract<ChoiceQuestion, { type: 'single_choice' }>).correct_option_id === id;
  }

  function toggleCorrect(id: string) {
    if (question.type === 'single_choice') {
      onChange({ ...question, correct_option_id: id });
      return;
    }
    const cur = question.correct_option_ids ?? [];
    onChange({
      ...question,
      correct_option_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    });
  }

  function setLabel(id: string, label: string) {
    onChange({
      ...question,
      options: question.options.map((o) => (o.id === id ? { ...o, label } : o)),
    } as ChoiceQuestion);
  }

  function removeOption(id: string) {
    const next = {
      ...question,
      options: question.options.filter((o) => o.id !== id),
    } as ChoiceQuestion;
    if (next.type === 'single_choice' && next.correct_option_id === id) {
      next.correct_option_id = '';
    }
    if (next.type === 'multi_select') {
      next.correct_option_ids = (next.correct_option_ids ?? []).filter((x) => x !== id);
    }
    onChange(next);
  }

  return (
    <div className="space-y-3">
      <QuestionCommonFields
        prompt={question.prompt}
        points={question.points}
        onPrompt={(prompt) => onChange({ ...question, prompt })}
        onPoints={(points) => onChange({ ...question, points })}
        graded={graded}
      />
      <ImageUploadInput
        label="Illustration (optional)"
        value={question.image_key}
        onChange={(url) => onChange({ ...question, image_key: url })}
      />
      <div className="space-y-2">
        <span className="text-sm font-medium text-fg-muted">
          Answers — tick the correct one{multi ? 's' : ''}
        </span>
        <SortableList
          ids={question.options.map((o) => o.id)}
          onReorder={(from, to) =>
            onChange({
              ...question,
              options: reorder(question.options, from, to),
            } as ChoiceQuestion)
          }
          className="space-y-2"
        >
          {question.options.map((o) => (
            <SortableRow key={o.id} id={o.id} className="flex items-center gap-2">
              {({ dragHandle }) => (
                <>
                  <DragHandle dragHandle={dragHandle} label="Reorder answer" />
                  <input
                    type={multi ? 'checkbox' : 'radio'}
                    name={`${question.id}-correct`}
                    checked={isCorrect(o.id)}
                    onChange={() => toggleCorrect(o.id)}
                    aria-label="Correct answer"
                  />
                  <input
                    value={o.label}
                    onChange={(e) => setLabel(o.id, e.target.value)}
                    placeholder="Answer text"
                    className={`${SHARED_INPUT_CLASS} mt-0 flex-1`}
                  />
                  <button
                    type="button"
                    aria-label="Remove answer"
                    disabled={question.options.length <= 2}
                    onClick={() => removeOption(o.id)}
                    className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              )}
            </SortableRow>
          ))}
        </SortableList>
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="h-4 w-4" />}
          onClick={() =>
            onChange({
              ...question,
              options: [...question.options, { id: genId('opt'), label: '' }],
            } as ChoiceQuestion)
          }
        >
          Add answer
        </Button>
      </div>
    </div>
  );
}
